import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { trigger, transition, style, animate } from '@angular/animations'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { PagesLoadedEvent } from 'ngx-extended-pdf-viewer'
import * as JSZip from 'jszip'

export interface ArquivoPreviewData {
  file: File
  titulo?: string
  validarProvaEscrita?: boolean
}

export interface ArquivoPreviewResult {
  confirmed: boolean
  pageCount: number | null
  fileName: string
  file: File
  identificacaoCandidato: boolean
  justificativaIdentificacao?: string
}

export interface ZipEntry {
  name: string
  size: number
}

@Component({
  selector: 'app-arquivo-preview-dialog',
  templateUrl: './arquivo-preview-dialog.component.html',
  styleUrls: ['./arquivo-preview-dialog.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(40%)', opacity: 0 }),
        animate('0.4s cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
    ]),
  ],
})
export class ArquivoPreviewDialogComponent implements OnInit, OnDestroy {
  isPdf = false
  isImage = false
  isVideo = false
  isZip = false
  isGeneric = false
  isTooLarge = false
  loading = true

  pdfSrc?: Blob
  mediaSrc?: string
  zipEntries: ZipEntry[] = []

  fileName: string
  fileSize: number
  fileType: string
  fileSizeMB: string

  pageCount: number | null = null
  confirmacaoChecked = false
  aplicandoMarcaDagua = false
  mostrarConfirmacaoReporte = false
  justificativaReporte = ''

  private readonly MAX_PREVIEW_SIZE = 50 * 1024 * 1024

  constructor(
    public dialogRef: MatDialogRef<ArquivoPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ArquivoPreviewData
  ) {
    this.fileName = data.file.name
    this.fileSize = data.file.size
    this.fileType = data.file.type || 'application/octet-stream'
    this.fileSizeMB = (data.file.size / (1024 * 1024)).toFixed(2)
  }

  ngOnInit(): void {
    if (this.fileSize > this.MAX_PREVIEW_SIZE) {
      this.isTooLarge = true
      this.loading = false
      return
    }
    this.loadPreview()
  }

  ngOnDestroy(): void {
    if (this.mediaSrc) {
      URL.revokeObjectURL(this.mediaSrc)
    }
  }

  onPagesLoaded(event: PagesLoadedEvent): void {
    this.pageCount = event.pagesCount
  }

  get isProvaEscrita(): boolean {
    return !!this.data.validarProvaEscrita
  }

  get isConfirmDisabled(): boolean {
    if (!this.isProvaEscrita || !this.isPdf) return false
    return !this.confirmacaoChecked || this.pageCount === null
  }

  confirmar(): void {
    if (this.isProvaEscrita) {
      this.dialogRef.close({
        confirmed: true,
        pageCount: this.pageCount,
        fileName: this.fileName,
        file: this.data.file,
        identificacaoCandidato: false,
      } as ArquivoPreviewResult)
    } else {
      this.dialogRef.close(true)
    }
  }

  cancelar(): void {
    if (this.isProvaEscrita) {
      this.dialogRef.close({
        confirmed: false,
        pageCount: this.pageCount,
        fileName: this.fileName,
        file: this.data.file,
        identificacaoCandidato: false,
      } as ArquivoPreviewResult)
    } else {
      this.dialogRef.close(false)
    }
  }

  reportarIdentificacao(): void {
    this.mostrarConfirmacaoReporte = true
  }

  cancelarReporte(): void {
    this.mostrarConfirmacaoReporte = false
  }

  async confirmarReporte(): Promise<void> {
    this.aplicandoMarcaDagua = true
    try {
      this.aplicandoMarcaDagua = false
      this.dialogRef.close({
        confirmed: true,
        pageCount: this.pageCount,
        fileName: this.fileName,
        file: this.data.file,
        identificacaoCandidato: true,
        justificativaIdentificacao: this.justificativaReporte.trim(),
      } as ArquivoPreviewResult)
    } catch (e) {
      console.error('Erro ao processar reporte:', e)
      this.aplicandoMarcaDagua = false
    }
  }

  onConfirmacaoChange(): void {
    if (this.confirmacaoChecked) {
      this.mostrarConfirmacaoReporte = false
    }
  }

  private loadPreview(): void {
    const type = this.fileType.toLowerCase()

    if (type === 'application/pdf') {
      this.isPdf = true
      this.pdfSrc = this.data.file
      this.loading = false
    } else if (type.startsWith('image/')) {
      this.isImage = true
      this.mediaSrc = URL.createObjectURL(this.data.file)
      this.loading = false
    } else if (type === 'video/mp4' || type.startsWith('video/')) {
      this.isVideo = true
      this.mediaSrc = URL.createObjectURL(this.data.file)
      this.loading = false
    } else if (type === 'application/zip' || type === 'application/x-zip-compressed' || this.fileName.endsWith('.zip')) {
      this.isZip = true
      this.loadZipContents()
    } else {
      this.isGeneric = true
      this.loading = false
    }
  }

  private async loadZipContents(): Promise<void> {
    try {
      const zip = await JSZip.loadAsync(this.data.file)
      this.zipEntries = []
      zip.forEach((relativePath, entry) => {
        if (!entry.dir) {
          this.zipEntries.push({
            name: relativePath,
            size: (entry as any)._data?.uncompressedSize || 0,
          })
        }
      })
      this.zipEntries.sort((a, b) => a.name.localeCompare(b.name))
    } catch {
      this.isZip = false
      this.isGeneric = true
    }
    this.loading = false
  }

  formatSize(bytes: number): string {
    if (bytes === 0) return '—'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
  }
}
